import { inject, Injectable } from '@angular/core';
import { LocalStorageService } from '../local-storage.service';
import { HttpClient } from '@angular/common/http';
import { environment } from '@environments/environment';
import { Observable, tap } from 'rxjs';
import { AuthResponse, AuthServiceInterface, LoginRequest } from '../../interfaces/auth.service.interface';
import { Router } from '@angular/router';
import { UserDefinitionService } from '../user-definition.service';

@Injectable({
    providedIn: 'root'
})
export class AuthService implements AuthServiceInterface {
    private readonly localStorageService = inject(LocalStorageService);
    private readonly http = inject(HttpClient);
    private readonly router = inject(Router);
    private readonly userDefinitionService = inject(UserDefinitionService);

    baseUrl = environment.apiUrl;

    getAuthToken() {
        return this.localStorageService.getItem('token');
    }

    isAuthenticated() {
        return this.localStorageService.getItem('token') !== null;
    }

    authenticate(accessToken: string | null): Observable<AuthResponse> {
        return this.http.get<AuthResponse>(`${this.baseUrl}/auth/access/${accessToken}`).pipe(
            tap((response) => {
                if (response.token) {
                    this.userDefinitionService.clearUserInfo();
                    this.localStorageService.setItem('token', response.token);
                }
            })
        );
    }

    login(username: string, password: string, recaptchaToken?: string): Observable<AuthResponse> {
        const body: LoginRequest = { username, password, recaptchaToken };
        return this.http.post<AuthResponse>(`${this.baseUrl}/auth/login`, body).pipe(
            tap((response) => {
                if (response.token) {
                    this.userDefinitionService.clearUserInfo();
                    this.localStorageService.setItem('token', response.token);
                }
            })
        );
    }

    refreshToken() {
        return this.http.get<AuthResponse>(`${this.baseUrl}/token/refresh`, { responseType: 'json' }).pipe(
            tap((response) => {
                if (response.token) {
                    this.localStorageService.setItem('token', response.token);
                }
            })
        );
    }

    logout() {
        this.userDefinitionService.clearUserInfo();
        this.localStorageService.removeItem('token');
        this.router.navigate(['/login']);
    }
}
